import { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { useTenant } from "../contexts/TenantContext";
import { fetchAuditLogs, fmtDate } from "../services/api";
import { Badge, Spinner, PageHeader } from "../components/ui";

const RESOURCES = ["", "contacts", "newsletters", "documents", "settings", "pipeline"];
const ACTIONS = ["", "create", "update", "delete", "send", "bulk-update", "move"];

const ACTION_COLORS = {
  create: { color: "#10b981", bg: "rgba(16,185,129,0.10)" },
  update: { color: "#3b82f6", bg: "rgba(59,130,246,0.10)" },
  delete: { color: "#ef4444", bg: "rgba(239,68,68,0.10)" },
  send: { color: "#8b5cf6", bg: "rgba(139,92,246,0.10)" },
  "bulk-update": { color: "#f59e0b", bg: "rgba(245,158,11,0.10)" },
  move: { color: "#6366f1", bg: "rgba(99,102,241,0.10)" },
};

const RESOURCE_COLORS = {
  contacts: { color: "#6366f1", bg: "rgba(99,102,241,0.10)" },
  newsletters: { color: "#8b5cf6", bg: "rgba(139,92,246,0.10)" },
  documents: { color: "#3b82f6", bg: "rgba(59,130,246,0.10)" },
  settings: { color: "#f59e0b", bg: "rgba(245,158,11,0.10)" },
  pipeline: { color: "#10b981", bg: "rgba(16,185,129,0.10)" },
};

function SmallBadge({ label, color, bg }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        color,
        background: bg,
        textTransform: "capitalize",
      }}
    >
      {label}
    </span>
  );
}

export default function AuditLog() {
  const tenant = useTenant();
  const tenantFonts = tenant.theme?.fonts || fonts;

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [resourceFilter, setResourceFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");

  const debounceRef = useRef(null);

  function loadLogs(resetOffset = false) {
    const currentOffset = resetOffset ? 0 : offset;
    if (resetOffset) setOffset(0);
    setLoading(true);
    fetchAuditLogs({
      from: fromDate || undefined,
      to: toDate || undefined,
      user: userFilter || undefined,
      resource: resourceFilter || undefined,
      action: actionFilter || undefined,
      limit,
      offset: currentOffset,
    })
      .then((data) => {
        setLogs(resetOffset ? data.logs : [...logs, ...data.logs]);
        setTotal(data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  // Debounced reload on filter changes
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadLogs(true), 300);
    return () => clearTimeout(debounceRef.current);
  }, [fromDate, toDate, userFilter, resourceFilter, actionFilter]);

  const hasMore = logs.length < total;

  const inputStyle = {
    padding: "7px 12px",
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    background: colors.bgCard,
    color: colors.text,
    fontSize: 13,
    fontFamily: tenantFonts.body,
    outline: "none",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
    minWidth: 120,
  };

  return (
    <div>
      <PageHeader
        title="Audit Log"
        subtitle={loading ? "Loading..." : `${total} events found`}
      />

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 20,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={inputStyle}
          placeholder="From"
          title="From date"
        />
        <span style={{ color: colors.textSecondary, fontSize: 13 }}>to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={inputStyle}
          placeholder="To"
          title="To date"
        />
        <div style={{ position: "relative" }}>
          <Search
            size={14}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: colors.textSecondary,
            }}
          />
          <input
            type="text"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            placeholder="Filter by user..."
            style={{ ...inputStyle, paddingLeft: 30, minWidth: 160 }}
          />
        </div>
        <select
          value={resourceFilter}
          onChange={(e) => setResourceFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="">All Resources</option>
          {RESOURCES.filter(Boolean).map((r) => (
            <option key={r} value={r}>
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="">All Actions</option>
          {ACTIONS.filter(Boolean).map((a) => (
            <option key={a} value={a}>
              {a.charAt(0).toUpperCase() + a.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 13,
            fontFamily: tenantFonts.body,
          }}
        >
          <thead>
            <tr
              style={{
                borderBottom: `1px solid ${colors.border}`,
                background: colors.bgHover,
              }}
            >
              {["Timestamp", "User", "Action", "Resource", "Resource ID", "Details"].map(
                (h) => (
                  <th
                    key={h}
                    style={{
                      padding: "10px 14px",
                      textAlign: "left",
                      fontWeight: 600,
                      color: colors.textSecondary,
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {logs.map((log, i) => {
              const ac = ACTION_COLORS[log.action] || {
                color: colors.textSecondary,
                bg: colors.bgHover,
              };
              const rc = RESOURCE_COLORS[log.resource] || {
                color: colors.textSecondary,
                bg: colors.bgHover,
              };
              return (
                <tr
                  key={log.id || i}
                  style={{
                    borderBottom: `1px solid ${colors.borderLight}`,
                  }}
                >
                  <td
                    style={{
                      padding: "10px 14px",
                      whiteSpace: "nowrap",
                      color: colors.textSecondary,
                      fontSize: 12,
                    }}
                  >
                    {fmtDate(log.timestamp)}
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      color: colors.text,
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {log.user}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <SmallBadge label={log.action} color={ac.color} bg={ac.bg} />
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <SmallBadge label={log.resource} color={rc.color} bg={rc.bg} />
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      color: colors.textSecondary,
                      fontSize: 12,
                      fontFamily: tenantFonts.mono || fonts.mono,
                      maxWidth: 180,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {log.resourceId || "—"}
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      color: colors.textSecondary,
                      fontSize: 12,
                      maxWidth: 300,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {log.details
                      ? Object.entries(log.details)
                          .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
                          .join(", ")
                      : "—"}
                  </td>
                </tr>
              );
            })}
            {logs.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    padding: 40,
                    textAlign: "center",
                    color: colors.textSecondary,
                  }}
                >
                  No audit events found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Load more */}
      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button
            onClick={() => {
              setOffset(logs.length);
              setLoading(true);
              fetchAuditLogs({
                from: fromDate || undefined,
                to: toDate || undefined,
                user: userFilter || undefined,
                resource: resourceFilter || undefined,
                action: actionFilter || undefined,
                limit,
                offset: logs.length,
              })
                .then((data) => {
                  setLogs([...logs, ...data.logs]);
                  setTotal(data.total);
                })
                .catch(() => {})
                .finally(() => setLoading(false));
            }}
            disabled={loading}
            style={{
              padding: "8px 24px",
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              background: colors.bgCard,
              color: colors.text,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: tenantFonts.body,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Loading..." : `Load More (${logs.length} of ${total})`}
          </button>
        </div>
      )}

      {loading && logs.length === 0 && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spinner size={20} />
        </div>
      )}
    </div>
  );
}
